import { useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { UnwindLog } from '../../../types/unwind-debug.types';
import { LEVEL_COLOR } from './levelColors';

export interface LogRowProps {
  log: UnwindLog;
}

export function LogRow({ log }: LogRowProps) {
  const [open, setOpen] = useState(false);
  const ts = new Date(log.createdAt);
  const tsLabel = `${ts.toLocaleTimeString()}.${String(ts.getMilliseconds()).padStart(3, '0')}`;
  const lvlColor = LEVEL_COLOR[log.level] ?? '#666';
  const hasMeta = log.meta && Object.keys(log.meta).length > 0;

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${lvlColor}`,
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        py: 0.5,
        pl: 1,
        pr: 1,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: '0.72rem',
        cursor: hasMeta ? 'pointer' : 'default',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
      }}
      onClick={() => hasMeta && setOpen((o) => !o)}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton size="small" sx={{ p: 0, visibility: hasMeta ? 'visible' : 'hidden' }}>
          {open ? <KeyboardArrowDownIcon fontSize="inherit" /> : <KeyboardArrowRightIcon fontSize="inherit" />}
        </IconButton>
        <Typography sx={{ width: 110, color: 'grey.500', fontSize: 'inherit' }}>{tsLabel}</Typography>
        <Typography sx={{ width: 50, color: lvlColor, fontWeight: 600, fontSize: 'inherit' }}>{log.level}</Typography>
        <Typography sx={{ width: 200, color: 'grey.400', fontSize: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {log.context}
        </Typography>
        {log.pairName && (
          <Typography sx={{ width: 180, color: 'grey.300', fontSize: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {log.pairName}
          </Typography>
        )}
        <Typography sx={{ flex: 1, color: 'grey.100', fontSize: 'inherit' }}>{log.message}</Typography>
      </Stack>
      {open && hasMeta && (
        <Box
          sx={{
            mt: 0.5,
            ml: 4,
            p: 1,
            bgcolor: 'rgba(0,0,0,0.3)',
            borderRadius: 1,
            color: 'grey.300',
            whiteSpace: 'pre',
            overflowX: 'auto',
            fontSize: '0.7rem',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {JSON.stringify(log.meta, null, 2)}
        </Box>
      )}
    </Box>
  );
}
